const STORAGE_KEY = 'filterutility:appliance:v1';

export function normalizeModel(value) {
  return String(value ?? '')
    .trim()
    .toLocaleUpperCase('en-US')
    .replace(/[^A-Z0-9]/g, '');
}

function normalizedRecordValue(record, keys) {
  for (const key of keys) {
    const value = record?.[key];
    if (value != null && String(value).trim()) return normalizeModel(value);
  }
  return '';
}

function recordFilterNumber(record) {
  return normalizedRecordValue(record, [
    'filter_number',
    'filter',
    'current_filter',
    'oem_filter_number',
    'oem',
  ]);
}

function recordApplianceType(record) {
  return normalizedRecordValue(record, ['appliance_type', 'appliance']);
}

export function checkCompatibility(records, brand, applianceType, model, filterNumber = '') {
  if (!Array.isArray(records)) return 'unknown';

  const normalizedBrand = normalizeModel(brand);
  const normalizedAppliance = normalizeModel(applianceType);
  const normalizedModel = normalizeModel(model);
  const normalizedFilter = normalizeModel(filterNumber);
  if (!normalizedModel && !normalizedFilter) return 'unknown';

  const matches = records.filter((record) => {
    if (!record || typeof record !== 'object') return false;
    const recordBrand = normalizeModel(record.brand);
    const recordAppliance = recordApplianceType(record);
    const brandMatches = !normalizedBrand || recordBrand === normalizedBrand;
    const applianceMatches = !normalizedAppliance || recordAppliance === normalizedAppliance;
    const modelMatches = !normalizedModel || normalizeModel(record.model) === normalizedModel;
    const filterMatches = !normalizedFilter || recordFilterNumber(record) === normalizedFilter;
    return brandMatches && applianceMatches && modelMatches && filterMatches;
  });

  if (!matches.length || matches.some((record) => typeof record.compatible !== 'boolean')) return 'unknown';
  const decisions = new Set(matches.map((record) => record.compatible));
  if (decisions.size !== 1) return 'unknown';
  return matches[0].compatible ? 'compatible' : 'incompatible';
}

function readSavedAppliance() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved && typeof saved === 'object' ? saved : null;
  } catch {
    return null;
  }
}

function saveAppliance(appliance) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appliance));
  } catch {}
  window.dispatchEvent(new CustomEvent('filterutility:appliance-change', { detail: appliance }));
}

function clearSavedAppliance() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  window.dispatchEvent(new CustomEvent('filterutility:appliance-change', { detail: null }));
}

function uniqueRecordValues(records, keyAliases) {
  const values = records
    .map((record) => keyAliases.map((key) => record?.[key]).find((value) => typeof value === 'string' && value.trim()))
    .filter(Boolean)
    .map((value) => value.trim());
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

if (typeof customElements !== 'undefined' && !customElements.get('fu-compatibility')) {
  customElements.define(
    'fu-compatibility',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.records = this.#readRecords();
        this.form = this.querySelector('form');
        this.result = this.querySelector('.fu-fit-result');
        this.savedRegion = this.querySelector('[data-saved-appliance]');
        if (!this.form || !this.result) return;

        this.#populateSelect('brand', uniqueRecordValues(this.records, ['brand']));
        this.#populateSelect('appliance_type', uniqueRecordValues(this.records, ['appliance_type', 'appliance']));

        const saved = readSavedAppliance();
        if (saved) {
          this.#restore(saved);
          this.#showSaved(saved);
          this.#renderResult(this.#check(saved), saved);
        }

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          const appliance = this.#formValues();
          if (!appliance.model && !appliance.filter_number) {
            this.form.elements.model?.focus();
            this.#renderResult('unknown', appliance, this.dataset.missingInput);
            return;
          }

          saveAppliance(appliance);
          this.#showSaved(appliance);
          this.#renderResult(this.#check(appliance), appliance);
        });

        this.querySelector('[data-clear-appliance]')?.addEventListener('click', () => {
          clearSavedAppliance();
          this.form.reset();
          this.savedRegion?.setAttribute('hidden', '');
          this.result.hidden = true;
          delete this.result.dataset.state;
          this.form.elements.model?.focus();
        });
      }

      #readRecords() {
        try {
          let records = JSON.parse(this.querySelector('[data-fit-records]')?.textContent || '[]');
          if (typeof records === 'string') records = JSON.parse(records);
          return Array.isArray(records) ? records : [];
        } catch {
          return [];
        }
      }

      #populateSelect(name, values) {
        const select = this.form.elements[name];
        if (!(select instanceof HTMLSelectElement)) return;
        for (const value of values) select.add(new Option(value, value));
      }

      #restore(saved) {
        for (const [name, value] of Object.entries({
          brand: saved.brand,
          appliance_type: saved.appliance_type,
          model: saved.model,
          filter: saved.filter_number,
        })) {
          const field = this.form.elements[name];
          if (!field || !value) continue;
          if (field instanceof HTMLSelectElement && ![...field.options].some((option) => option.value === value)) {
            field.add(new Option(value, value));
          }
          field.value = value;
        }
      }

      #formValues() {
        return {
          brand: String(this.form.elements.brand?.value || '').trim(),
          appliance_type: String(this.form.elements.appliance_type?.value || '').trim(),
          model: String(this.form.elements.model?.value || '').trim(),
          filter_number: String(this.form.elements.filter?.value || '').trim(),
        };
      }

      #check(appliance) {
        return checkCompatibility(
          this.records,
          appliance.brand,
          appliance.appliance_type,
          appliance.model,
          appliance.filter_number,
        );
      }

      #showSaved(appliance) {
        if (!this.savedRegion) return;
        const identifier = appliance.model || appliance.filter_number;
        const label = this.savedRegion.querySelector('[data-saved-appliance-label]');
        if (!identifier || !label) {
          this.savedRegion.hidden = true;
          return;
        }
        label.textContent = [appliance.brand, appliance.appliance_type, identifier].filter(Boolean).join(' · ');
        this.savedRegion.hidden = false;
      }

      #renderResult(state, appliance, overrideMessage = '') {
        this.result.dataset.state = state;
        let message = overrideMessage || this.dataset[state] || '';
        const identifier = appliance.model || appliance.filter_number;
        if (state === 'compatible' && identifier && this.dataset.savedCompatible) {
          message = `${this.dataset.savedCompatible.replace('{identifier}', identifier)} ${message}`.trim();
        }
        this.result.textContent = message;
        this.result.hidden = false;
      }
    },
  );
}

if (typeof customElements !== 'undefined' && !customElements.get('fu-product-fit')) {
  customElements.define(
    'fu-product-fit',
    class extends HTMLElement {
      connectedCallback() {
        this.handleApplianceChange = (event) => this.#render(event.detail);
        window.addEventListener('filterutility:appliance-change', this.handleApplianceChange);
        this.#render(readSavedAppliance());
      }

      disconnectedCallback() {
        window.removeEventListener('filterutility:appliance-change', this.handleApplianceChange);
      }

      #records() {
        try {
          let records = JSON.parse(this.querySelector('[data-fu-card-fit-records]')?.textContent || '[]');
          if (typeof records === 'string') records = JSON.parse(records);
          return Array.isArray(records) ? records : [];
        } catch {
          return [];
        }
      }

      #render(appliance) {
        const identifier = appliance?.model || appliance?.filter_number;
        if (!identifier) {
          this.hidden = true;
          this.removeAttribute('data-state');
          return;
        }

        const state = checkCompatibility(
          this.#records(),
          appliance.brand,
          appliance.appliance_type,
          appliance.model,
          appliance.filter_number,
        );
        if (state === 'unknown') {
          this.hidden = true;
          this.removeAttribute('data-state');
          return;
        }

        const label = this.querySelector('[data-fu-card-fit-label]');
        if (!label) {
          this.hidden = true;
          return;
        }
        const template = state === 'compatible' ? this.dataset.compatible : this.dataset.incompatible;
        label.textContent = (template || '').replace('{identifier}', identifier);
        this.dataset.state = state;
        this.hidden = false;
      }
    },
  );
}
